import { PlateProvider } from '@udecode/plate';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import _ from 'lodash';
import styled from 'styled-components';

import ActionBar from 'components/text_editor_new/components/action_bar/action_bar';
import { AITextCompletionButtons } from 'components/text_editor_new/components/controls/ai_text_rewrite_controls';
import { AITextCompletionContextProvider } from 'components/text_editor_new/plugins/ai/components/ai_text_completion_context';
import AssigneeSelector from 'components/composer/shared/assignee_selector';
import { Attachment, Emoji } from 'components/composer/shared/editor_action_buttons_new';
import {
  AnswerPanelControl,
  BoldItalicUnderlineControls,
  ListControls,
  Separator,
} from 'components/composer/shared/editor_controls_new';
import { EditorContainer } from 'components/composer/shared/editor_new';
import ForgottenAttachmentModal from 'components/composer/email/forgotten_attachment_modal';
import ForgottenTextModal from 'components/composer/email/forgotten_text_modal';
import CloseCurrentComposition from 'actions/composition/close_current_composition';
import DatePicker from 'components/composer/shared/date_picker';
import Composer, { Header, NewHeaderText } from 'components/composer/shared/composer_new';
import ComposerEditor, { ComposerSubjectLineEditor } from 'components/composer/shared/composer_editor';
import connect from 'components/lib/connect';
import createAITextCompletion from 'components/text_editor_new/plugins/ai/ai_text_completion';
import createAITextCompletionHighlight from 'components/text_editor_new/plugins/ai/ai_text_completion/ai_text_completion_highlight';
import { createUrlHighlightPlugin } from 'components/text_editor_new/plugins/inline_links/create_url_highlight_plugin';
import EditorErrors from 'components/composer/shared/editor_errors';
import FontColorControls from 'components/composer/shared/font_color_controls';
import From from 'components/composer/email/from';
import getInitialHtml from 'components/composer/email/get_initial_html';
import { isMacOS } from 'scripts/lib/browser_detector';
import InsertInlineImageButton from 'components/composer/shared/insert_inline_image_button_new';
import { LinkButton } from 'components/composer/inline_links/link_button';
import { LinkEditor } from 'components/composer/inline_links/link_editor';
import Recipients from 'components/composer/email/recipients';
import SelectionInitializer from 'components/composer/email/selection_initializer';
import SendThirdPartyEmail from 'actions/composer/send_third_party_email';
import serializeHtmlBody from 'components/text_editor_new/lib/serialize_html_body';
import { serializePlaintext, serializePlaintextFragment } from 'components/text_editor_new/lib/serialize_plaintext';
import { SnippetContentType } from 'models/answers/snippet';
import { SubjectContainerNew } from 'components/composer/email/subject';
import TableMenu from 'components/composer/shared/editor_table_controls_new';
import TextAlignmentMenu from 'components/composer/shared/editor_alignment_controls_new';
import useAIComposerOptions from 'components/text_editor_new/plugins/ai/ai_text_completion/use_ai_composer_options';
import useComposer from 'components/composer/shared/use_composer';
import useEditor from 'components/composer/shared/use_editor';
import useForgottenTextModal from 'components/composer/email/use_forgotten_text_modal';
import useInitialValue from 'components/text_editor_new/hooks/use_initial_value';
import usePlaintextEditorPlugins from 'components/text_editor_new/hooks/use_plaintext_editor_plugins';
import useRichTextEditorPlugins from 'components/text_editor_new/hooks/use_rich_text_editor_plugins';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import { useSnippetAttachments } from 'components/composer/email/use_snippet_attachments';
import withRefreshEditorWhenFlagsChange from 'components/composer/shared/with_refresh_editor_when_flags_change';
import withStaticId from 'components/customer/lib/with_static_id';
import { useSubmitOnHotkey } from 'components/composer/shared/use_submit_on_hotkey';

const AI_HERO_PLUGINS = [createAITextCompletion(), createAITextCompletionHighlight()];

const StyledHeader = styled(Header)`
  flex-wrap: wrap;
  row-gap: 4px;
`;

const StyledFollowUp = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 4px 0;
`;

export function ThirdPartyEmailComposerBase({
  defaultBody,
  hasNewAttachments,
  includeAIFeatures,
  isSending,
  onCloseComposition,
  requestorId,
}) {
  const { t } = useTranslation();
  const composerProps = useComposer({
    action: SendThirdPartyEmail,
    options: { createContent },
  });
  const editorProps = useEditor('bodyHtml', composerProps, { serializer: serializeHtmlBody });
  const subjectEditorProps = useEditor('subjectHtml', composerProps, {
    attr: 'subject',
    dontAutofocus: true,
    serializer: serializePlaintext,
    editorId: 'subjectHtml',
  });

  // Memoized because extraPlugins is a dependency of useRichTextEditorPlugins' own useMemo: a fresh
  // array each render rebuilds the whole plugin set and a new Slate editor.
  const extraPlugins = useMemo(
    () => (includeAIFeatures ? [createUrlHighlightPlugin(), ...AI_HERO_PLUGINS] : [createUrlHighlightPlugin()]),
    [includeAIFeatures]
  );
  const editorRef = useRichTextEditorPlugins(composerProps, editorProps, extraPlugins);
  const subjectRef = usePlaintextEditorPlugins(composerProps, subjectEditorProps);

  const { disableSubmit } = useAIComposerOptions();

  // With greetings and signatures, we want to place the initial cursor position in an expected place for agents.
  // If we have a greeting _and_ a signature, for example, the cursor goes in the middle. If we're loading a draft,
  // on the other hand, the cursor will just go at the end (hence the -1).
  const [initialHtml, initialParagraphIndex] = useMemo(() => {
    if (editorProps.initialHtml) {
      return [editorProps.initialHtml, -1];
    }

    return getInitialHtml(defaultBody, editorRef);
  }, []);

  const initialSubjectValue = useInitialValue(subjectEditorProps.initialHtml, subjectRef.current);
  const initialSubjectPlaintext = serializePlaintextFragment(initialSubjectValue || []);
  useSnippetAttachments(defaultBody);

  const {
    onSubmit: decoratedOnSubmit,
    showForgottenTextModal,
    clearShowForgottenTextModal,
    forgottenAttachmentText,
    clearForgottenAttachmentText,
  } = useForgottenTextModal(composerProps.onSubmit, editorProps.editorRef.current, defaultBody);

  // Override default `onCancel` because in case of emails, we need to account for the default values,
  // i.e. greeting, signature and/or initial subject. Also, take attachments into account. While a send is
  // out the draft stays put: closing it would delete the uploads the send carries.
  const onCancel = () => {
    if (isSending) return;
    const [defaultHtml] = getInitialHtml(defaultBody, editorRef);
    const defaultFragment = defaultHtml ? editorRef.current.getSaturatedFragment(defaultHtml) : [];
    const defaultPlaintext = serializePlaintextFragment(defaultFragment);

    const defaultValues = { body: _.trim(defaultPlaintext), subject: _.trim(initialSubjectPlaintext) };
    const currentValues = {
      body: _.trim(serializePlaintext(editorRef.current)),
      subject: _.trim(serializePlaintext(subjectRef.current)),
    };

    // Did they change at least one non-whitespace character in either body or the subject?
    if (_.isEqual(defaultValues, currentValues) && !hasNewAttachments) {
      onCloseComposition();
    } else {
      composerProps.setIsCancelling(true);
    }
  };

  // Register keyboard hotkeys that result in submitting the composition. For historic reason, we use both Meta+Enter
  // and Ctrl+Enter on Mac and just Ctrl+Enter on Windows
  const keyboardHotkeys = ['Mod+Enter'];
  if (isMacOS()) keyboardHotkeys.push('Control+Enter');
  const submitUnlessSending = evt => !isSending && decoratedOnSubmit(evt);
  useSubmitOnHotkey(editorRef.current, submitUnlessSending, keyboardHotkeys);

  const authoringControls = (
    <>
      <AnswerPanelControl />
      {includeAIFeatures ? (
        <>
          <AITextCompletionButtons />
          <Separator />
        </>
      ) : null}
    </>
  );

  const composer = (
    <Composer
      defaultEditor={editorRef.current}
      defaultEditorId={editorProps.editorId}
      defaultOnChange={editorProps.onChange}
      disableSubmit={disableSubmit || isSending}
      excludeErrors
      includeAttachments={!isSending}
      initialHtml={initialHtml}
      submitText={t('Send')}
      {...composerProps}
      onCancel={onCancel}
      onSubmit={decoratedOnSubmit}
    >
      {initialParagraphIndex > -1 ? <SelectionInitializer paragraphIndex={initialParagraphIndex} /> : null}
      <StyledHeader>
        <NewHeaderText>{t('Side conversation')}</NewHeaderText>
        <Recipients />
        <From />
      </StyledHeader>
      <SubjectContainerNew editorProps={subjectEditorProps}>
        <PlateProvider
          editor={subjectRef.current}
          id={subjectEditorProps.editorId}
          initialValue={initialSubjectValue}
          normalizeInitialValue
          onChange={subjectEditorProps.onChange}
        >
          <ComposerSubjectLineEditor
            autoFocus={false}
            minHeight="0px"
            placeholder={t('Enter a subject')}
            width="100%"
            {...subjectEditorProps}
          />
        </PlateProvider>
      </SubjectContainerNew>

      <EditorContainer excludeAnswerMenuButton includeAttachments>
        <ComposerEditor
          autoFocus={!!editorProps.initialHtml || initialParagraphIndex === -1}
          includeAIFeatures={includeAIFeatures}
          padding="8px"
          placeholder={t('Type to respond')}
          requestorId={requestorId}
          {...editorProps}
          initialHtml={initialHtml}
        />
        <LinkEditor hotKey="mod+k" />
      </EditorContainer>

      <StyledFollowUp>
        <AssigneeSelector />
        <DatePicker label={t('Follow up by')} />
      </StyledFollowUp>

      <ActionBar>
        {authoringControls}
        <BoldItalicUnderlineControls {...editorProps} />
        <FontColorControls {...editorProps} />
        <TextAlignmentMenu {...editorProps} />
        <ListControls {...editorProps} />
        <TableMenu {...editorProps} />
        <>
          <Separator />
          <LinkButton />
        </>
        <Emoji {...editorProps} />
        <Attachment />
        <InsertInlineImageButton {...editorProps} />
      </ActionBar>

      <EditorErrors />

      {forgottenAttachmentText ? (
        <ForgottenAttachmentModal
          onCancel={clearForgottenAttachmentText}
          onSubmit={evt => {
            composerProps.onSubmit(evt);
            clearForgottenAttachmentText();
          }}
          text={forgottenAttachmentText}
        />
      ) : null}
      {showForgottenTextModal ? (
        <ForgottenTextModal
          onCancel={clearShowForgottenTextModal}
          onSubmit={evt => {
            composerProps.onSubmit(evt);
            clearShowForgottenTextModal();
          }}
        />
      ) : null}
    </Composer>
  );

  return composer;
}

function ThirdPartyEmailComposerWithAIFeatures(props) {
  const isFeatureEnabled = useIsFeatureEnabled();
  const includeAIFeatures = isFeatureEnabled('enableEmailComposerHeroAI');

  let composer = <ThirdPartyEmailComposerContainer {...props} includeAIFeatures={includeAIFeatures} />;

  if (includeAIFeatures) {
    return (
      <AITextCompletionContextProvider requestorId={props.requestorId}>{composer}</AITextCompletionContextProvider>
    );
  }
  return composer;
}

function createContent(editor, attr, content) {
  if (attr === 'bodyHtml') {
    return {
      ...content,
      bodyPlain: serializePlaintext(editor),
    };
  }

  return content;
}
const MemoizedComposer = React.memo(ThirdPartyEmailComposerBase);
const ThirdPartyEmailComposerContainer = connect(mapStateToProps, mapExecuteToProps)(MemoizedComposer);
ThirdPartyEmailComposerBase.propTypes = {
  defaultBody: PropTypes.shape({
    greetingSnippet: PropTypes.object,
    signatureSnippet: PropTypes.object,
  }),
  hasNewAttachments: PropTypes.bool,
  isSending: PropTypes.bool,
  onCloseComposition: PropTypes.func.isRequired,
  requestorId: PropTypes.string.isRequired,
};

function mapStateToProps({ getProvider }) {
  const compositionProvider = getProvider('compositions');
  const currentAgent = getProvider('currentAgent').get() || {};
  const currentLocation = getProvider('currentLocation').get();
  const compositionId = currentLocation?.currentCompositionId;
  const { emailGreetingSnippetId, emailSignatureSnippetId } = currentAgent;

  const defaultBody = {
    greetingSnippet: getProvider('currentAgentSnippets').findBy({ id: emailGreetingSnippetId }),
    signatureSnippet: getProvider('currentAgentSnippets').findBy({ id: emailSignatureSnippetId }),
  };

  let hasNewAttachments = false;
  if (compositionId) {
    const composition = compositionProvider.find(compositionId);
    if (composition?.hasAttachments()) {
      // check whether we have any attachments that were added by the agent (i.e. were not transferred from either
      // the greeting or the signature)
      const snippetAttachments = _.reduce(
        defaultBody,
        (acc, answer) => {
          if (!answer) return acc;

          const attachments = [];
          _.forEach(answer.contents, contentBlock => {
            const ids = _.map(contentBlock[SnippetContentType.ANY_CHANNEL]?.attachments, attachment => attachment.id);
            attachments.push(...ids);
          });
          return [...acc, ...attachments];
        },
        []
      );

      hasNewAttachments = _.some(composition.attachments, attachment => !snippetAttachments.includes(attachment.id));
    }
  }

  return {
    defaultBody,
    hasNewAttachments,
    isSending: !!compositionId && getProvider('inFlightSends').get().has(compositionId),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onCloseComposition: () => executeAction(CloseCurrentComposition),
  };
}
export const ThirdPartyEmailComposer = withStaticId(
  withRefreshEditorWhenFlagsChange(ThirdPartyEmailComposerWithAIFeatures),
  'requestorId'
);

export default ThirdPartyEmailComposer;
